// FRONT-3249 (behind eddy.model-picker-redesign): the composer's model control
// for a GenAI Hub workspace, as one dense menu instead of a button plus a
// slider. The button names the model, its cloud and the thinking level.
//
// Opened, it does not float over the chat: it opens INSIDE the composer box.
// The box shows the menu in place of the text line (the draft stays where it
// is, only hidden) and resizes around it. The menu lists the person's favourite
// models (plus the one in use and their default), a Thinking row that slides
// every level in beside them, and "All models…", which brings the full chooser
// into the same box. While it is open, ONE button stands exactly where the
// button was — Back inside a pane, Close on the menu — so the control that
// opened it is the one that leaves it.

import { createPortal } from 'react-dom';
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { CaretDownIcon, CaretLeftIcon, CaretRightIcon, CheckIcon, XIcon } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { THINKING_STOPS, TOP_TIER_STOPS, stopIndexOf } from '~/lib/thinking-stops';
import type { ThinkingLevel } from '~/lib/types/llm';
import { shortModelName, type PickerModelEntry, type PickerPrefs } from './model-picker-logic';
import { allModelsHeight, cloudName } from './hub-picker-logic';

/** What the composer box shows: its text line, the quick menu, or the full chooser. */
export type ModelPanel = 'closed' | 'menu' | 'all';

export interface HubModelMenuProps {
  prefs: PickerPrefs;
  /** The model the next turn uses. */
  activeId: string | null;
  thinking: ThinkingLevel | null | undefined;
  /** The model in use has Very high and Pro of its own (else it thinks at High there). */
  topTiers: boolean;
  /** Why the control cannot be used right now; null = usable. */
  disabledReason: string | null;
  onPick: (id: string | null) => void;
  onThinking: (level: ThinkingLevel | null) => void;
  panel: ModelPanel;
  onPanelChange: (panel: ModelPanel) => void;
  /** The element inside the composer box the panel renders into; without one nothing can open. */
  panelSlot: HTMLElement | null;
  /** The full chooser, shown in the box for "All models…". */
  allModels: ReactNode;
}

/** "Sonnet 5.5 / AWS" for a listed model — the same cloud names as the picker; its short name when the Hub names no cloud. */
export function modelMenuLabel(m: Pick<PickerModelEntry, 'displayName' | 'hub'>): string {
  const name = shortModelName(m.hub?.displayName ?? m.displayName);
  const cloud = cloudName(m.hub?.providerLabel);
  return cloud ? `${name} / ${cloud}` : name;
}

/**
 * The menu's quick list: the person's starred models in the table's order,
 * plus the model in use and their default when not starred — each once.
 */
export function quickModels(prefs: PickerPrefs, activeId: string | null): PickerModelEntry[] {
  const starred = new Set(prefs.starred ?? []);
  const keep = new Set<string>([...starred]);
  if (activeId) keep.add(activeId);
  if (prefs.defaultModel?.id) keep.add(prefs.defaultModel.id);
  return prefs.models.filter((m) => keep.has(m.id));
}

export function thinkingLabel(level: ThinkingLevel | null | undefined): string {
  return THINKING_STOPS[stopIndexOf(level)]!.label;
}

const ROW =
  'flex w-full items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-left text-sm outline-none transition-colors hover:border-border focus-visible:border-border focus-visible:ring-2 focus-visible:ring-ring/50';
/** Back and Close: the same quiet button as the model button they stand in for — no border, no fill at rest, light gray under the pointer. */
export const FOOT_BUTTON =
  'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:bg-muted/60 focus-visible:text-foreground outline-none';

export function HubModelMenu({
  prefs,
  activeId,
  thinking,
  topTiers,
  disabledReason,
  onPick,
  onThinking,
  panel,
  onPanelChange,
  panelSlot,
  allModels,
}: HubModelMenuProps) {
  const active = prefs.models.find((m) => m.id === activeId);
  const label = active ? modelMenuLabel(active) : prefs.defaultModel ? shortModelName(prefs.defaultModel.displayName) : 'Model';
  const quick = quickModels(prefs, activeId);
  const defaultId = prefs.defaultModel?.id ?? null;
  const stop = stopIndexOf(thinking);
  const open = panel !== 'closed' && panelSlot !== null;
  // Inside the menu: the models, or the thinking levels slid in beside them.
  const [sub, setSub] = useState<'models' | 'thinking'>('models');
  const bodyRef = useRef<HTMLDivElement>(null);
  const [allHeight, setAllHeight] = useState<number | null>(null);

  const close = () => {
    setSub('models');
    onPanelChange('closed');
  };
  // Back: out of Thinking to the models, out of "All models" to the menu.
  const canGoBack = panel === 'all' || sub === 'thinking';
  const back = () => {
    if (panel === 'all') onPanelChange('menu');
    else setSub('models');
  };

  // Escape closes the panel and nothing behind it (the chat window would close too).
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      closeRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  // The full chooser fills the chat window; measured as it opens and while the window resizes.
  useLayoutEffect(() => {
    if (panel !== 'all' || !panelSlot) {
      setAllHeight(null);
      return;
    }
    const measure = () => {
      const box = panelSlot.closest<HTMLElement>('[data-composer-box]');
      const chat = document.querySelector<HTMLElement>('[data-testid="chat-window-panel"]');
      setAllHeight(
        allModelsHeight({
          viewport: window.innerHeight,
          chat: chat ? chat.getBoundingClientRect().height : null,
          // What the box holds besides the chooser: the button row, its padding and borders.
          boxChrome: box ? box.getBoundingClientRect().height - panelSlot.getBoundingClientRect().height : 0,
        }),
      );
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [panel, panelSlot]);

  // Focus follows the pane: its first row, so the keyboard can carry on from the button.
  useEffect(() => {
    if (!open) return;
    bodyRef.current?.querySelector<HTMLElement>('[data-menu-row]')?.focus();
  }, [open, panel, sub]);

  const onBodyKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const rows = [...(bodyRef.current?.querySelectorAll<HTMLElement>('[data-menu-row]') ?? [])];
    const at = rows.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? at + 1 : at - 1;
      rows[(next + rows.length) % rows.length]?.focus();
    } else if (e.key === 'ArrowLeft' && sub === 'thinking' && panel === 'menu') {
      e.preventDefault();
      back();
    }
  };

  const modelsPane = (
    <div key="models" role="menu" aria-label="Model" className="animate-in fade-in-0 duration-150 motion-safe:slide-in-from-left-6" data-testid="composer-model-pane">
      {quick.map((m) => (
        <button
          key={m.id}
          type="button"
          role="menuitem"
          data-menu-row
          data-testid={`composer-model-item-${m.id}`}
          // Choosing the default model means following the default.
          onClick={() => {
            onPick(m.id === defaultId ? null : m.id);
            close();
          }}
          className={ROW}
        >
          <span className="min-w-0 flex-1 truncate">{modelMenuLabel(m)}</span>
          {m.id === defaultId && (
            <span className="shrink-0 rounded-full border border-border px-1.5 text-[10px] text-muted-foreground">Default</span>
          )}
          <CheckIcon size={14} weight="bold" className={cn('shrink-0 text-primary', m.id === activeId ? 'visible' : 'invisible')} />
        </button>
      ))}
      {quick.length > 0 && <div role="separator" className="-mx-1 my-1 h-px bg-border" />}
      <button type="button" role="menuitem" data-menu-row data-testid="composer-thinking-menu" onClick={() => setSub('thinking')} className={ROW}>
        <span className="flex-1">Thinking</span>
        <span className="text-muted-foreground">{thinkingLabel(thinking)}</span>
        <CaretRightIcon size={12} weight="bold" className="shrink-0 text-muted-foreground" />
      </button>
      <button type="button" role="menuitem" data-menu-row data-testid="composer-model-all" onClick={() => onPanelChange('all')} className={ROW}>
        All models…
      </button>
    </div>
  );

  const thinkingPane = (
    <div key="thinking" role="menu" aria-label="Thinking" className="animate-in fade-in-0 duration-150 motion-safe:slide-in-from-right-6" data-testid="composer-thinking-pane">
      {THINKING_STOPS.map((s, i) => (
        <button
          key={s.label}
          type="button"
          role="menuitemradio"
          aria-checked={i === stop}
          data-menu-row
          data-testid={`composer-thinking-${s.label}`}
          onClick={() => {
            if (s.level !== (thinking ?? null)) onThinking(s.level);
            close();
          }}
          className={ROW}
        >
          <span className="flex-1">{s.label}</span>
          {/* One small note, only where it changes a decision: the two dear levels. */}
          {TOP_TIER_STOPS.has(i) && (
            <span className="rounded-full border border-border px-1.5 text-[10px] text-muted-foreground">
              {topTiers ? 'slower, costs more' : 'High on this model'}
            </span>
          )}
          <CheckIcon size={14} weight="bold" className={cn('shrink-0 text-primary', i === stop ? 'visible' : 'invisible')} />
        </button>
      ))}
    </div>
  );

  const body = (
    <div
      ref={bodyRef}
      data-testid="composer-model-panel"
      data-panel={panel}
      onKeyDown={onBodyKeyDown}
      className={cn('flex flex-col overflow-hidden', panel === 'all' ? 'px-3 pt-3' : 'p-2')}
      style={panel === 'all' && allHeight ? { height: allHeight } : undefined}
    >
      {panel === 'all' ? (
        <div key="all" className="flex min-h-0 flex-1 flex-col animate-in fade-in-0 duration-150 motion-safe:slide-in-from-right-6" data-testid="composer-all-models">
          {allModels}
        </div>
      ) : sub === 'models' ? (
        modelsPane
      ) : (
        thinkingPane
      )}
    </div>
  );

  return (
    <>
      {open ? (
        // Where the button was, ONE button: Back when there is a pane to return to (inside Thinking and
        // "All models"), Close on the menu itself.
        <div className="flex items-center" data-testid="composer-model-foot">
          {canGoBack ? (
            <button type="button" data-testid="composer-thinking-back" onClick={back} className={FOOT_BUTTON}>
              <CaretLeftIcon size={11} weight="bold" className="shrink-0" />
              Back
            </button>
          ) : (
            <button type="button" data-testid="composer-model-close" onClick={close} className={FOOT_BUTTON}>
              <XIcon size={11} weight="bold" className="shrink-0" />
              Close
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          data-testid="composer-model-button"
          data-model-id={activeId ?? undefined}
          disabled={disabledReason !== null || panelSlot === null}
          onClick={() => {
            setSub('models');
            onPanelChange('menu');
          }}
          aria-label={`Model: ${label}, thinking: ${thinkingLabel(thinking)}${disabledReason ? ` — ${disabledReason}` : ''}`}
          // No width cap: the button takes the room the model and its cloud need; only a row that has run out of space shortens the name.
          className="inline-flex min-w-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground disabled:opacity-50"
        >
          <span className="truncate text-foreground">{label}</span>
          <span data-testid="composer-thinking-label" className="shrink-0">
            {thinkingLabel(thinking)}
          </span>
          <CaretDownIcon size={11} weight="bold" className="shrink-0" />
        </button>
      )}
      {open && panelSlot ? createPortal(body, panelSlot) : null}
    </>
  );
}
